import React from 'react';
import styled from 'styled-components';
import { FiArrowLeft, FiArrowRight, FiCheck } from 'react-icons/fi';
import { PrimaryButton, GhostButton } from './surveyTheme.styles';

// One-question-at-a-time Previous / progress / Next(-or-Submit) row, shared
// by the authenticated SurveyPlayer, the public magic-link
// PublicSurveyPlayer, and the instructor SurveyPreview — mirrors the quiz
// player's progress-then-nav pattern (QuizNavigation.jsx), restyled to the
// survey feature's own gold theme instead of duplicating it per-page.

const NavRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
  flex-wrap: wrap;

  /* ProgressBlock drops to its own line above (order: -1; flex-basis: 100%
     below), leaving Previous/Next sharing the second line — row-gap is the
     space above that line, column-gap is what actually separates the two
     buttons, so only column-gap gets the extra +20% (14px -> 17px). */
  @media (max-width: 480px) {
    row-gap: 12px;
    column-gap: 17px;
  }
`;

const ProgressBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  flex: 1 1 auto;
  max-width: 240px;

  /* Full width and first in line, so Previous/Next drop to their own
     centered row underneath it instead of squeezing in beside it. */
  @media (max-width: 480px) {
    order: -1;
    flex-basis: 100%;
    max-width: none;
  }
`;

const ProgressLabel = styled.span`
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--text-primary);
`;

// Narrower than the full-width BarTrack/BarFill used elsewhere (e.g. the
// results page) — this one just marks progress through a short list of
// questions, not a data value, so it doesn't need to span the card.
//
// 80% (not 100%) of ProgressBlock's own width, and centered by that block's
// align-items: center — ProgressBlock itself stays full size so Previous/
// Next don't shift, only the visible track/fill narrows. On mobile, narrowed
// a further 30% (80% * 0.7 = 56%) — ProgressBlock is full-width there too,
// so without this the track would otherwise span the whole screen.
const ProgressTrack = styled.div`
  width: 80%;
  height: 8px;
  background: var(--border-color);
  border-radius: 999px;
  overflow: hidden;

  @media (max-width: 480px) {
    width: 56%;
  }
`;

const ProgressFill = styled.div`
  height: 100%;
  border-radius: 999px;
  background: var(--primary-color);
  width: ${props => props.$pct || 0}%;
  transition: width 0.4s ease;
`;

// Flat instead of PrimaryButton's usual gradient.
const NextButton = styled(PrimaryButton)`
  background: var(--primary-color);
`;

const ClosingMessageCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: var(--info-light, #63B3ED);
  background: color-mix(in srgb, var(--info-color, #3182CE) 12%, var(--background-primary, #fff));
  border: 1px solid var(--info-color, #3182CE);
  border-radius: 12px;
  padding: 1rem 1.125rem;
  margin-top: 20px;
  box-sizing: border-box;
  width: 100%;

  @media (max-width: 640px) {
    gap: 0.625rem;
    padding: 0.875rem;
    border-radius: 10px;
  }
`;

const ClosingMessageBody = styled.div`
  flex: 1;
  min-width: 0;
`;

// The survey-author's optional note shown on the last question only, right
// before the student submits (e.g. "please also fill out the paper form").
const ClosingMessageText = styled.p`
  margin: 0;
  color: var(--text-primary);
  font-size: 0.95rem;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;

  @media (max-width: 640px) {
    font-size: 0.9rem;
    line-height: 1.45;
  }
`;

export default function SurveyPlayerNav({
  currentQuestionIndex, totalQuestions, goToPrevious, goToNext, onSubmit, submitting, submitDisabled, isReadOnly,
  closingMessage, hasClosingStep,
}) {
  if (totalQuestions === 0) return null;

  const totalSteps = totalQuestions + (hasClosingStep ? 1 : 0);
  const isClosingStep = hasClosingStep && currentQuestionIndex === totalSteps - 1;
  const isLast = currentQuestionIndex === totalSteps - 1;
  // The closing message is a real, navigable step -- Previous/Next both work
  // on it, and totalSteps/isLast/isClosingStep above all need to know it's
  // there. But it isn't a question, and counting it in the displayed "X of Y"
  // and the progress bar made a 2-question survey read as "2 of 3" while
  // answering the last real question. Cap what's DISPLAYED at totalQuestions,
  // so the closing step shows as fully complete ("2 of 2", 100%) instead of a
  // phantom extra question.
  const displayedStep = Math.min(currentQuestionIndex + 1, totalQuestions);
  const progress = (displayedStep / totalQuestions) * 100;

  return (
    <>
      {isClosingStep && !isReadOnly && closingMessage && (
        <ClosingMessageCard>
          <ClosingMessageBody>
            <ClosingMessageText>{closingMessage}</ClosingMessageText>
          </ClosingMessageBody>
        </ClosingMessageCard>
      )}
      <NavRow>
        <GhostButton type="button" onClick={goToPrevious} disabled={currentQuestionIndex === 0}>
          <FiArrowLeft size={16} /> Previous
        </GhostButton>

        <ProgressBlock>
          <ProgressLabel>{displayedStep} of {totalQuestions}</ProgressLabel>
          <ProgressTrack><ProgressFill $pct={progress} /></ProgressTrack>
        </ProgressBlock>

        {isLast ? (
          !isReadOnly && (
            <PrimaryButton type="button" onClick={onSubmit} disabled={submitting || submitDisabled}>
              <FiCheck size={16} /> {submitting ? 'Submitting…' : 'Submit Survey'}
            </PrimaryButton>
          )
        ) : (
          <NextButton type="button" onClick={goToNext}>
            Next <FiArrowRight size={16} />
          </NextButton>
        )}
      </NavRow>
    </>
  );
}
